import React, { useEffect, useState } from 'react';
import { styled } from '@mui/system';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { GET_PATIENT_PHOTO } from 'op-components/PatientCard/PatientCardQueries';
import { useLazyQuery } from '@apollo/client';
import useInterval from 'shared-components/utils/useInterval';
import { Avatar, Popover, Skeleton } from '@mui/material';
interface PhotoStyledProps {
  size: number;
}
const PhotoWrapper = styled('div')<PhotoStyledProps>`
  height: ${(props: PhotoStyledProps) => props.size}px;
  width: ${(props: PhotoStyledProps) => props.size}px;
  & img {
    object-fit: cover;
    width: ${(props: PhotoStyledProps) => props.size}px;
    height: ${(props: PhotoStyledProps) => props.size}px;
  }
  & svg {
    width: ${(props: PhotoStyledProps) => props.size}px;
    height: ${(props: PhotoStyledProps) => props.size}px;
  }
`;

const StyledPopupWindow = styled('div')`
  height: 320px;
  width: 320px;
  border-radius: 32px;
  overflow: hidden;
  & img {
    object-fit: cover;
    height: 320px;
    width: 320px;
    border-radius: 32px;
    border-radius: 32px;
    border: 4px solid ${(props) => props.theme.palette.grey[300]};
  }
`;

interface PhotoWidgetProps {
  patientId?: string | number;
  url?: string; // allow override of patient (and default) photo
  enableOverlay?: boolean;
  numRetries?: number;
  size?: string;
  deferFetch?: boolean;
}

const PHOTO_SIZE = {
  sm: 32,
  reg: 48,
  md: 64,
  lg: 128,
};

const PhotoWidget = (props: PhotoWidgetProps): JSX.Element => {
  const [isPhotoLoadError, setPhotoLoadError] = useState(true);
  const [countTimer, setCountTimer] = useState(0);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [canFetchPhoto, setCanFetchPhoto] = useState(!props.deferFetch);
  const numRetries = props.numRetries ? props.numRetries : 3; // Retries 10 times and gives up
  const size = props.size ? PHOTO_SIZE[props.size as keyof typeof PHOTO_SIZE] : PHOTO_SIZE['md'];
  const DefaultIcon = <AccountCircleIcon sx={{ width: size, height: size, color: 'grey.300' }} />;

  const [fetchPatientPhoto, { data, loading, called }] = useLazyQuery(GET_PATIENT_PHOTO);
  const resolvedPhotoUrl = props.url || data?.patientPhoto?.photoUrl;

  useEffect(() => {
    if (props.deferFetch) {
      // Start photo loading after initial paint so it does not compete with critical UI queries.
      const timerId = window.setTimeout(() => {
        setCanFetchPhoto(true);
      }, 0);
      return () => window.clearTimeout(timerId);
    }
    return undefined;
  }, [props.deferFetch]);

  useEffect(() => {
    if (!canFetchPhoto || !!props.url || !props.patientId || called) {
      return;
    }
    fetchPatientPhoto({ variables: { patientId: props.patientId } });
  }, [canFetchPhoto, props.url, props.patientId, called, fetchPatientPhoto]);

  useInterval(
    () => {
      setCountTimer(countTimer + 1);
    },
    isPhotoLoadError && !!resolvedPhotoUrl && countTimer < numRetries ? 10000 : null,
  );
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  return (
    <>
      <PhotoWrapper
        data-test-id="photo-wrapper"
        size={size}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}>
        {loading ? (
          <Skeleton variant="circular" animation="pulse" width={size} height={size} />
        ) : resolvedPhotoUrl ? (
          <>
            {isPhotoLoadError && DefaultIcon}
            <Avatar
              sx={{
                width: size,
                height: size,
                display: isPhotoLoadError ? 'none' : 'flex',
              }}
              src={resolvedPhotoUrl}
              slotProps={{
                img: {
                  onLoad: (e: React.SyntheticEvent<HTMLImageElement>) => {
                    const img = e.currentTarget;
                    // Check if image has valid dimensions (not empty/corrupt)
                    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                      setPhotoLoadError(false);
                    } else {
                      setPhotoLoadError(true);
                    }
                  },
                  onError: () => {
                    setPhotoLoadError(true);
                  },
                },
              }}
            />
          </>
        ) : (
          DefaultIcon
        )}
      </PhotoWrapper>
      {!loading && !isPhotoLoadError && resolvedPhotoUrl && (
        <Popover
          id="mouse-over-popover"
          sx={{
            pointerEvents: 'none',
            backgroundColor: 'transparent',
            borderRadius: '28px',
            boxShadow: 'none',
            '& .MuiPopover-paper': {
              boxShadow: 'none',
            },
          }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus>
          <StyledPopupWindow>
            <img src={resolvedPhotoUrl} />
          </StyledPopupWindow>
        </Popover>
      )}
    </>
  );
};

export default PhotoWidget;
